import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, BookOpen, Briefcase, Link2, Calendar, Camera, DollarSign, LayoutGrid, Mic, MessageCircle, Mail } from 'lucide-react';

const SECTIONS = [
  {
    id: 'voice-recap',
    icon: Mic,
    title: 'AI Voice Recap',
    faqs: [
      ['What is AI Voice Recap?', 'Instead of typing a long recap after your shift, tap the mic, talk for 30-60 seconds about how it went, and AI transcribes + organizes everything for you to review before submitting.'],
      ['How do I use it?', 'When your post-event recap opens (right after you clock out), tap "Record voice recap". Hold and speak normally — we auto-stop at 2 minutes. Then review the AI draft (rating + highlights + concerns + summary) and edit anything before hitting Submit.'],
      ['Do I have to use voice?', "No. If you'd rather type, tap \"I'd rather type my recap\" inside the modal and use the classic flow."],
      ['Does AI submit it for me?', "No — you always review and confirm. The AI just drafts so you don't have to start from a blank screen."],
      ['Why are voice recaps better?', 'Faster completion, less typing, more natural feedback, and AI keeps the structure consistent (rating, highlights, concerns, summary) so admins and clients can act on it quickly.'],
      ['Privacy & data', 'Audio is transcribed via Whisper and the transcript is stored alongside the recap. Only your admin and the booking client can see it — same access as the typed recap.'],
    ],
  },
  {
    id: 'booking-actions',
    icon: LayoutGrid,
    title: 'Booking Actions menu',
    faqs: [
      ['Where do I upload photos for a booking?', 'Open My Bookings → tap the three-dot Actions on the booking card → "Upload Photos". You can also tap "Actions" inside the booking detail page.'],
      ['Where do I submit my Post-Event Recap?', 'Same Actions menu → "Submit Recap". The recap opens in a modal so you can rate and add notes the moment your shift ends.'],
      ['How do I submit reimbursements (parking, supplies, etc.)?', 'Actions menu → "Reimbursements". Add the amount, category, and a receipt photo. Admins review and approve from their queue.'],
      ['How do I see the dress code?', 'Actions → "Dress Code" or Booking Details → Dress Code section. You\'ll see the exact dress code image, title, description, and checklist the client picked when they created the gig.'],
      ['How do I see the grooming requirements?', 'Actions → "Grooming" or Booking Details → Grooming. Shows tattoo, facial hair, hair, and piercing requirements the client selected.'],
      ['Where are the event instructions and parking info?', 'Actions → "Instructions" or Booking Details. We pull arrival instructions, parking, and the on-site contact straight from the brief.'],
      ['Where do I see my pay for this booking?', 'Actions → "Payment Details" jumps you to your Earnings page filtered for the booking.'],
      ['How do I add the shift to my calendar?', 'Actions → "Add to Calendar" downloads a .ics file. Multi-day bookings export every shift in one file.'],
      ['How do I release a gig if I can no longer make it?', 'Actions → "Release Gig" then confirm. The slot re-opens immediately for backup talent. Last-minute drops may affect your reliability score — message your admin first if it\'s within 48h.'],
    ],
  },
  {
    icon: Briefcase,
    title: 'Finding & applying to gigs',
    faqs: [
      ['How do I find gigs?', 'Home feed shows gigs we matched to your profile. Open any card, tap Apply, pick your preferred date if the shift has multiple dates, and confirm.'],
      ['Why can\'t I see a gig?', 'The most common reason is profile completion — missing IDV, a profile photo, or geocoded address. Open Profile → Completion checklist.'],
      ['What is a "Primary" vs "Backup" vs "Extra"?', 'Primary is booked. Backup is next in line if a Primary drops. Extra is a waitlist we tap for last-minute fills.'],
    ],
  },
  {
    icon: Link2,
    title: 'Your personal booking link',
    faqs: [
      ['Where do I find my link?', 'Home page → "My Booking Link" card. Tap Copy to share it anywhere — IG bio, email, SMS.'],
      ['Do I pay a fee?', 'iEvntür takes a 15% service fee from the total the client pays. You keep 85% and we handle invoicing + payment.'],
      ['Can I edit the link?', 'Tap Regenerate to create a new slug. The old URL stops working.'],
    ],
  },
  {
    icon: Camera,
    title: 'Clock-in, clock-out, and selfies',
    faqs: [
      ['Why does it ask for a selfie?', 'It\'s how we confirm you showed up in person — required for contractor payouts and for some clients\' compliance. A 1-second capture, no filters.'],
      ['I forgot to clock out — what now?', 'Message your admin / shift lead. They can manually record the clock-out time with a reason.'],
      ['GPS said I\'m off-site but I\'m inside.', 'Tap "Request GPS override" and add a quick note. Admin approves or rejects from the Attendance panel.'],
    ],
  },
  {
    icon: Calendar,
    title: 'Shifts & schedule',
    faqs: [
      ['How do I confirm a shift?', 'Home → shift card → Confirm. You\'ll get reminder push + SMS 48h and 24h before.'],
      ['Can I drop a shift?', 'Up to 48h before — tap Drop + pick a reason. Within 48h, contact admin first or it may affect your reliability score.'],
    ],
  },
  {
    icon: DollarSign,
    title: 'Pay & invoices',
    faqs: [
      ['When do I get paid?',
       'Payment timing depends on the specific Gig and who is responsible for payment. Each Gig displays its own payment window (or objectively calculable due date) before you accept, plus who pays you: iEvntür (for iEvntür Paid / Managed Gigs) or the client directly (for Client Paid, Marketplace, or Direct Booking). Any mandatory state or local rule controls when it applies. There is no universal weekly payout — always check the specific Gig.'],
      ['Can I use Instant Pay?',
       'Instant Payout is optional and eligibility-based, not automatic. Availability is determined per-Gig based on payment type, your account status, and Stripe requirements (e.g., a verified debit card). If Instant Pay is available for a specific completed Gig it will show as an option; otherwise it is not available for that opportunity.'],
      ['Who is paying me for this Gig?',
       'The responsible payment party is shown on the Gig before you accept and again on the confirmation screen. iEvntür Paid / Managed Gigs are paid by iEvntür. Client Paid, Marketplace, Referral, and Direct Booking opportunities are paid by the third-party business identified on the Gig — iEvntür does not assume that third party\'s payment obligation except where required by law.'],
      ['Where do I enter bank details?', 'Profile → Payouts → Direct Deposit. Double-check the routing number; bounced payments delay everyone on the run.'],
    ],
  },
];

const TalentHelp = () => {
  const navigate = useNavigate();
  // Iter-234 — auto-scroll to the section matching the URL hash
  // (e.g. /talent/help#booking-actions from the What's New modal).
  React.useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    }
  }, []);
  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-help-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="talent-help-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-cyan-600" />
            Docs & FAQs
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* 2026-06 — Chat with Evie is the PRIMARY support option on every
            screen size; email is the secondary fallback at the bottom. */}
        <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-white">
          <CardContent className="py-4 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-cyan-500 text-white flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900">Chat with Evie · Virtual Assistant</div>
              <div className="text-xs text-gray-600 mt-0.5">
                Ask about payments and expected payment dates, bookings, applications, confirmations or your account — Evie answers using your real account details.
              </div>
            </div>
            <Button
              size="sm"
              className="bg-cyan-500 hover:bg-cyan-600 text-white shrink-0"
              onClick={() => navigate('/talent/support?chat=1')}
              data-testid="talent-help-chat-btn"
            >
              Start chat
            </Button>
          </CardContent>
        </Card>

        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.title} id={s.id || undefined} className={s.id ? 'scroll-mt-20' : undefined}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="w-4 h-4 text-cyan-600" />
                  {s.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {s.faqs.map(([q, a], i) => (
                  <div key={i}>
                    <div className="font-medium text-sm text-gray-900">{q}</div>
                    <div className="text-sm text-gray-600 mt-0.5">{a}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}

        <Card>
          <CardContent className="py-4 space-y-3">
            <Button
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
              onClick={() => navigate('/talent/support?chat=1')}
              data-testid="talent-help-chat-btn-bottom"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Chat with Evie
            </Button>
            <div className="text-center text-xs text-gray-500">
              Prefer email? Reach us at{' '}
              <a href="mailto:support@ievntur.com" className="text-cyan-700 hover:underline">
                support@ievntur.com
              </a>
              <span className="inline-flex items-center gap-1 ml-1 text-gray-400"><Mail className="w-3 h-3" /></span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile sticky chat bar — chat is never more than one tap away */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-200 px-4 py-3 z-20"
           style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
        <Button
          className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
          onClick={() => navigate('/talent/support?chat=1')}
          data-testid="talent-help-chat-sticky"
        >
          <MessageCircle className="w-4 h-4 mr-2" /> Chat with Evie · Virtual Assistant
        </Button>
      </div>
      <div className="sm:hidden h-20" />
    </div>
  );
};

export default TalentHelp;
